import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Divider, message, Modal, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type FiservPaymentModuleSpecsFragment } from '../../../../api/fragments/FiservPaymentModuleSpecs';
import {
    DeleteFiservPaymentSettingsDocument,
    type DeleteFiservPaymentSettingsMutation,
    type DeleteFiservPaymentSettingsMutationVariables,
} from '../../../../api/mutations/deleteFiservPaymentSettings';

export type FiservPaymentSettingActionsProps = {
    module: FiservPaymentModuleSpecsFragment;
    paymentSetting: FiservPaymentModuleSpecsFragment['paymentSettings'][number];
};

const FiservPaymentSettingActions = ({ module, paymentSetting }: FiservPaymentSettingActionsProps) => {
    const moduleId = module.id;
    const paymentSettingId = paymentSetting.id;

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { t } = useTranslation(['fiservPaymentModuleDetails']);

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('fiservPaymentModuleDetails:paymentSettings.deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('fiservPaymentModuleDetails:paymentSettings.deleteModal.content'),
            okText: t('fiservPaymentModuleDetails:paymentSettings.deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('fiservPaymentModuleDetails:paymentSettings.deleteModal.cancelButton'),
            onOk: async () => {
                message.loading({
                    content: t('fiservPaymentModuleDetails:paymentSettings.deleteModal.messages.submitting'),
                    key: 'primary',
                    duration: 0,
                });

                await apolloClient
                    .mutate<DeleteFiservPaymentSettingsMutation, DeleteFiservPaymentSettingsMutationVariables>({
                        mutation: DeleteFiservPaymentSettingsDocument,
                        variables: { moduleId, id: paymentSettingId },
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });

                message.success({
                    content: t('fiservPaymentModuleDetails:paymentSettings.deleteModal.messages.success'),
                    key: 'primary',
                });
            },
        });
    }, [apolloClient, moduleId, paymentSettingId, t]);

    return (
        <Space size="small" split={<Divider type="vertical" />}>
            <Button
                onClick={() => navigate(`/admin/system/modules/${moduleId}/fiservPayment/${paymentSettingId}`)}
                size="small"
                style={{ padding: 0 }}
                type="link"
            >
                {t('fiservPaymentModuleDetails:paymentSettingsTable.actions.update')}
            </Button>
            <Button onClick={deleteAction} size="small" style={{ padding: 0 }} type="link">
                {t('fiservPaymentModuleDetails:paymentSettingsTable.actions.delete')}
            </Button>
        </Space>
    );
};

export default FiservPaymentSettingActions;
